import type { I18n } from '@learnforge/i18n';
import { normalPdf } from '../prob/normal';
import { createSvgEl } from '@learnforge/demo-render';
import { niceTickStep, formatTick } from '@learnforge/demo-math/numeric';

export interface PmfBar {
  x: number;
  p: number;
  className?: string;
}

export interface PmfChartOptions {
  xDomain: [number, number];
  yMax?: number;
  bars: readonly PmfBar[];
  empiricalBars?: readonly PmfBar[];
  markers?: { x: number; labelKey: string }[];
  normalOverlay?: { mu: number; sigma: number; opacity?: number };
  /** Overlay a step-function CDF from the theoretical bars. Forces yMax to 1. */
  showCdf?: boolean;
  /**
   * Pascal-row values (one per integer x in 0..n). When set, the chart reserves
   * extra top padding and prints each value above the corresponding bar — a visual
   * anchor that P(X=k) = C(n,k)·p^k·(1-p)^(n-k).
   */
  pascalRowValues?: readonly number[];
  xLabelKey?: string;
  yLabelKey?: string;
  i18n: I18n<string>;
}

export interface PmfChartHandle {
  group: SVGGElement;
  update(opts: Partial<PmfChartOptions>): void;
  destroy(): void;
}

const VIEW_W = 800;
const VIEW_H = 400;
const PAD_L = 56;
const PAD_R = 24;
const PAD_T = 28;
const PAD_B = 56;

function formatBinomCoef(v: number): string {
  const n = Math.round(v);
  if (n < 100000) return String(n);
  const exp = Math.floor(Math.log10(n));
  const mantissa = n / Math.pow(10, exp);
  return `${mantissa.toFixed(1)}e${exp}`;
}

export function createPmfChart(parent: SVGGElement, opts: PmfChartOptions): PmfChartHandle {
  const group = createSvgEl('g', { class: 'pb-pmf' });
  parent.appendChild(group);
  let current: PmfChartOptions = opts;

  function render(): void {
    group.replaceChildren();
    const [xMin, xMax] = current.xDomain;
    const allBars = [...current.bars, ...(current.empiricalBars ?? [])];
    // CDF reaches 1 by construction, so a CDF overlay forces the y-axis to span [0, 1].
    const yMax = current.showCdf
      ? 1
      : (current.yMax ?? (Math.max(0.01, Math.max(0, ...allBars.map((b) => b.p)) * 1.1) || 1));
    // Pascal row sits in the top margin: bump padding so chips don't overlap bars.
    // Skip rendering when the chips would be too narrow to be readable.
    const wantsPascal = (current.pascalRowValues?.length ?? 0) > 0;
    const xRangeForChip = xMax - xMin;
    const chipWMax = ((VIEW_W - PAD_L - PAD_R) / (xRangeForChip + 1)) * 0.9;
    const hasPascal = wantsPascal && chipWMax >= 22;
    const padT = hasPascal ? 60 : PAD_T;
    const toX = (x: number) => PAD_L + ((x - xMin) / (xMax - xMin)) * (VIEW_W - PAD_L - PAD_R);
    const toY = (p: number) => VIEW_H - PAD_B - (p / yMax) * (VIEW_H - padT - PAD_B);
    const xRange = xMax - xMin;
    const barW = Math.max(2, ((VIEW_W - PAD_L - PAD_R) / (xRange + 1)) * 0.8);

    // gridlines + axes
    const yStep = niceTickStep(yMax, 5);
    for (let y = 0; y <= yMax + 1e-9; y += yStep) {
      const yPx = toY(y);
      const grid = createSvgEl('line', {
        class: 'pb-axis-grid',
        x1: PAD_L,
        y1: yPx,
        x2: VIEW_W - PAD_R,
        y2: yPx,
      });
      group.appendChild(grid);
      const tickLbl = createSvgEl('text', {
        class: 'pb-axis-tick-label',
        x: PAD_L - 8,
        y: yPx,
        'text-anchor': 'end',
        'dominant-baseline': 'central',
      });
      tickLbl.textContent = formatTick(y, yStep);
      group.appendChild(tickLbl);
    }

    const xStep = niceTickStep(xRange, 8);
    const xStart = Math.ceil(xMin / xStep) * xStep;
    for (let x = xStart; x <= xMax + 1e-9; x += xStep) {
      const xPx = toX(x);
      const tick = createSvgEl('line', {
        class: 'pb-axis-tick',
        x1: xPx,
        y1: VIEW_H - PAD_B,
        x2: xPx,
        y2: VIEW_H - PAD_B + 4,
      });
      group.appendChild(tick);
      const lbl = createSvgEl('text', {
        class: 'pb-axis-tick-label',
        x: xPx,
        y: VIEW_H - PAD_B + 8,
        'text-anchor': 'middle',
        'dominant-baseline': 'hanging',
      });
      lbl.textContent = formatTick(x, xStep);
      group.appendChild(lbl);
    }

    // axis lines (drawn on top of grid so they sit clean above bars)
    const xAxis = createSvgEl('line', {
      class: 'pb-axis-line',
      x1: PAD_L,
      y1: VIEW_H - PAD_B,
      x2: VIEW_W - PAD_R,
      y2: VIEW_H - PAD_B,
    });
    const yAxis = createSvgEl('line', {
      class: 'pb-axis-line',
      x1: PAD_L,
      y1: padT,
      x2: PAD_L,
      y2: VIEW_H - PAD_B,
    });
    group.appendChild(xAxis);
    group.appendChild(yAxis);

    // axis labels
    if (current.xLabelKey) {
      const lbl = createSvgEl('text', {
        class: 'pb-axis-label',
        x: PAD_L + (VIEW_W - PAD_L - PAD_R) / 2,
        y: VIEW_H - 12,
        'text-anchor': 'middle',
      });
      lbl.textContent = current.i18n.t(current.xLabelKey);
      group.appendChild(lbl);
    }
    if (current.yLabelKey) {
      const lbl = createSvgEl('text', {
        class: 'pb-axis-label',
        x: 0,
        y: 0,
        'text-anchor': 'middle',
        transform: `translate(16, ${padT + (VIEW_H - padT - PAD_B) / 2}) rotate(-90)`,
      });
      lbl.textContent = current.i18n.t(current.yLabelKey);
      group.appendChild(lbl);
    }

    for (const bar of current.bars) {
      const rect = createSvgEl('rect', {
        class: `pb-pmf-bar ${bar.className ?? ''}`.trim(),
        x: toX(bar.x) - barW / 2,
        y: toY(bar.p),
        width: barW,
        height: Math.max(0, VIEW_H - PAD_B - toY(bar.p)),
      });
      group.appendChild(rect);
    }

    if (current.empiricalBars) {
      for (const bar of current.empiricalBars) {
        const rect = createSvgEl('rect', {
          class: 'pb-pmf-bar pb-pmf-bar-empirical',
          x: toX(bar.x) - barW / 4,
          y: toY(bar.p),
          width: barW / 2,
          height: Math.max(0, VIEW_H - PAD_B - toY(bar.p)),
        });
        group.appendChild(rect);
      }
    }

    if (current.normalOverlay) {
      const { mu, sigma, opacity = 0.6 } = current.normalOverlay;
      const samples = 240;
      const points: string[] = [];
      for (let i = 0; i <= samples; i++) {
        const x = xMin + ((xMax - xMin) * i) / samples;
        const y = normalPdf(x, mu, sigma);
        points.push(`${toX(x).toFixed(2)},${toY(y).toFixed(2)}`);
      }
      const path = createSvgEl('polyline', {
        class: 'pb-pmf-normal-overlay',
        points: points.join(' '),
        fill: 'none',
        opacity,
      });
      group.appendChild(path);
    }

    if (current.showCdf && current.bars.length > 0) {
      // Step polyline: horizontal at running total, vertical jump at each bar x.
      const sorted = [...current.bars].sort((a, b) => a.x - b.x);
      const pts: string[] = [];
      const dots: { x: number; cum: number }[] = [];
      pts.push(`${toX(xMin).toFixed(2)},${toY(0).toFixed(2)}`);
      let cum = 0;
      for (const b of sorted) {
        pts.push(`${toX(b.x).toFixed(2)},${toY(cum).toFixed(2)}`);
        cum += b.p;
        pts.push(`${toX(b.x).toFixed(2)},${toY(cum).toFixed(2)}`);
        dots.push({ x: b.x, cum });
      }
      pts.push(`${toX(xMax).toFixed(2)},${toY(cum).toFixed(2)}`);
      const cdf = createSvgEl('polyline', {
        class: 'pb-pmf-cdf',
        points: pts.join(' '),
        fill: 'none',
      });
      group.appendChild(cdf);
      for (const d of dots) {
        const dot = createSvgEl('circle', {
          class: 'pb-pmf-cdf-dot',
          cx: toX(d.x),
          cy: toY(d.cum),
          r: 3,
        });
        group.appendChild(dot);
      }
    }

    if (current.markers) {
      for (const m of current.markers) {
        const line = createSvgEl('line', {
          class: 'pb-pmf-marker',
          x1: toX(m.x),
          y1: padT,
          x2: toX(m.x),
          y2: VIEW_H - PAD_B,
        });
        group.appendChild(line);
        const txt = createSvgEl('text', {
          class: 'pb-pmf-marker-label',
          x: toX(m.x),
          y: padT - 4,
          'text-anchor': 'middle',
        });
        txt.textContent = current.i18n.t(m.labelKey);
        group.appendChild(txt);
      }
    }

    if (hasPascal && current.pascalRowValues) {
      const chipY = padT - 32;
      const chipH = 22;
      const chipW = Math.max(20, Math.min(46, chipWMax));
      for (let k = 0; k < current.pascalRowValues.length; k++) {
        const value = current.pascalRowValues[k]!;
        const cx = toX(k);
        const chip = createSvgEl('g', { class: 'pb-pmf-pascal-chip' });
        chip.appendChild(
          createSvgEl('rect', {
            class: 'pb-pmf-pascal-chip-bg',
            x: cx - chipW / 2,
            y: chipY,
            width: chipW,
            height: chipH,
            rx: 4,
          }),
        );
        const txt = createSvgEl('text', {
          class: 'pb-pmf-pascal-chip-text',
          x: cx,
          y: chipY + chipH / 2,
          'text-anchor': 'middle',
          'dominant-baseline': 'central',
        });
        txt.textContent = formatBinomCoef(value);
        chip.appendChild(txt);
        group.appendChild(chip);
      }
    }
  }

  render();
  return {
    group,
    update(next) {
      current = { ...current, ...next };
      render();
    },
    destroy() {
      group.remove();
    },
  };
}
